'use client';
import React from 'react';
import Link from 'next/link';
import styles from './Button.module.css';

type ButtonProps = {
  children: React.ReactNode;
  variant?: 'primary' | 'outline' | 'ghost';
  className?: string;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  as?: 'button' | 'a';
  target?: string;
  rel?: string;
  suppressHydrationWarning?: boolean;
  id?: string;
  name?: string;
  title?: string;
};

export default function Button({
  children,
  variant = 'primary',
  className = '',
  href,
  onClick,
  type = 'button',
  disabled = false,
  as,
  target,
  rel,
  suppressHydrationWarning,
  id,
  name,
  title,
}: ButtonProps) {
  const buttonClasses = `${styles.button} ${styles[variant]} ${className}`;

  // If href is provided and as is not explicitly set to 'button', render as Link or anchor
  if (href) {
    // If it's an internal link (doesn't start with http or https), use Next.js Link
    if (!href.startsWith('http') && !as) {
      return (
        <Link
          href={href}
          className={buttonClasses}
          suppressHydrationWarning={suppressHydrationWarning}
          id={id}
          title={title}
        >
          {children}
        </Link>
      );
    }

    // If as="a" or it's an external link, render as anchor tag
    if (as === 'a' || href.startsWith('http')) {
      return (
        <a
          href={href}
          className={buttonClasses}
          onClick={onClick}
          target={target}
          rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
          suppressHydrationWarning={suppressHydrationWarning}
          id={id}
          title={title}
        >
          {children}
        </a>
      );
    }
  }

  // Default to button
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      suppressHydrationWarning={suppressHydrationWarning}
      id={id}
      name={name}
      title={title}
    >
      {children}
    </button>
  );
}
